import type { FC } from 'react'
import { useContext, useEffect, useState } from 'react'
import { RegularButton } from 'fronton-react'
import { Table } from 'antd'
import { Modal } from 'fronton-react'
import { Layout } from 'shared/components/layout/Layout'
import { UserInfoResponse } from 'redux/interfaces'
import { useDispatch, useSelector } from 'react-redux'
import { userInfoSelector, calendarsSelector } from 'redux/selectors'
import './myCalendars.scss'
import { deleteCalendar, deleteSupplierCalendar, getCalendarList } from 'redux/actions'
import { getColumns } from 'pages/my-calendars'
import { ICalendar } from './add-calendar'
import { SupplierCalendarsContext } from 'contexts/SupplierCalendarsContext'
import type { CalendarsStatuses, CalendarStatus } from 'shared/interfaces/types'
import { checkSuperAdminRole } from 'shared/utils'

interface ModalState {
    status: boolean
    body: string
    actions: JSX.Element | null
}

export const MyCalendarsPage: FC = () => {
    const dispatch = useDispatch()
    const calendars = useSelector(calendarsSelector).list
    const [tableData, setTableData] = useState<ICalendar[]>([])
    const userInfo: UserInfoResponse = useSelector(userInfoSelector)
    const [modal, setModal] = useState<ModalState>({
        status: false,
        body: '',
        actions: null
    })
    const { setCalendarId, setCalendarsStatuses } = useContext(SupplierCalendarsContext)

    const isSuperAdmin = checkSuperAdminRole()

    useEffect(() => {
        if (calendars.length > 0) {
            const preparedCalendarsStatuses = calendars.reduce((acc, calendar) => {
                acc[calendar.id] = calendar.status as CalendarStatus
                return acc
            }, {} as CalendarsStatuses)

            setCalendarsStatuses(preparedCalendarsStatuses)
        }

        if (userInfo.isAdmin) {
            setTableData(calendars.filter((el) => el.status !== 'Worksheet'))
            return
        }
        setTableData(calendars)
    }, [calendars, userInfo])

    const onDeleteButtonClick = (calendarId: string, calendarName: string) => {
        setModal({
            status: true,
            body: `Вы уверены, что хотите удалить календарь "${calendarName}"? Удаленные календари не подлежат восстановлению`,
            actions: (
                <>
                    <RegularButton
                        size="s"
                        variant="primary"
                        className="modal-content-btn"
                        onClick={() => removeWorksheet(calendarId, calendarName)}
                    >
                        Да
                    </RegularButton>
                    <RegularButton
                        size="s"
                        variant="primary"
                        className="modal-content-btn"
                        style={{
                            paddingLeft: '15px',
                            paddingRight: '15px'
                        }}
                        onClick={() =>
                            setModal({
                                ...modal,
                                status: false
                            })
                        }
                    >
                        Нет
                    </RegularButton>
                </>
            )
        })
    }

    const removeWorksheet = (calendarId: string, calendarName: string) => {
        setModal({
            ...modal,
            status: false
        })

        if (isSuperAdmin) {
            dispatch(deleteSupplierCalendar.request(calendarId))
        } else {
            dispatch(deleteCalendar.request(calendarId))
        }

        setModal({
            status: true,
            body: `Календарь "${calendarName}" удален из ваших календарей`,
            actions: (
                <RegularButton
                    size="s"
                    variant="primary"
                    className="modal-content-btn"
                    onClick={() =>
                        setModal({
                            ...modal,
                            status: false
                        })
                    }
                >
                    Ок
                </RegularButton>
            )
        })
    }

    useEffect(() => {
        dispatch(getCalendarList.request())
        setCalendarId('')
    }, [])

    return (
        <Layout
            userInfo={userInfo}
            showToolbar
            toolbarTitle={userInfo.isAdmin ? 'Календари поставщика' : 'Мои календари'}
        >
            <div className="my-calendars">
                <Modal
                    size="s"
                    key="deleteModal"
                    className="delete-modal"
                    show={modal.status}
                    closeButton={false}
                    onClose={() =>
                        setModal({
                            ...modal,
                            status: false
                        })
                    }
                    overlayType="medium"
                >
                    <div className="modal-content">
                        <div className="modal-content-body">{modal.body}</div>
                        <div className="modal-content-actions">{modal.actions}</div>
                    </div>
                </Modal>
                <Table
                    className="table"
                    columns={getColumns(onDeleteButtonClick)}
                    dataSource={tableData}
                />
            </div>
        </Layout>
    )
}
